import type { FC } from 'react';
import { useState } from 'react';
import { Button, List, Tooltip } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import type { TClientInvoiceDocumentRes } from 'api/swagger';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { useGetOrderInvoiceDocuments } from 'client-app/api/documents/use-get-order-invoice-documents.ts';
import { DocumentListItem } from 'client-app/components/documents/document-list-item/document-list-item.tsx';
import { DocumentsModal } from 'client-app/components/documents/documents-modal/documents-modal.tsx';
import { getStatusDescription } from './get-status-description.ts';
import styles from './invoice-documents-list-modal.module.scss';
import type { IInvoiceDocumentsListModalProps } from './types';

export const InvoiceDocumentsListModal: FC<IInvoiceDocumentsListModalProps> = ({ orderId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: documents, isLoading } = useGetOrderInvoiceDocuments(orderId, { enabled: isModalOpen });
  const toggleModalStatus = () => setIsModalOpen((prevState) => !prevState);

  const renderDescription = (document: TClientInvoiceDocumentRes) => {
    return (
      <div className={styles.documentInfo}>
        <span className={styles.statusDescription}>
          {getStatusDescription(document.invoiceStatus, document.endPaymentPeriod)}
        </span>
        <span className={styles.documentDate}>{formatDate(document.createdAt)}</span>
      </div>
    );
  };

  return (
    <>
      <Tooltip title='Invoice documents' placement='bottom'>
        <Button icon={<AuditOutlined />} onClick={toggleModalStatus} />
      </Tooltip>
      <DocumentsModal
        isModalOpen={isModalOpen}
        isLoading={isLoading}
        documentsAvailable={!!documents?.length}
        title='Invoice documents'
        onClose={toggleModalStatus}>
        <List
          size='small'
          itemLayout='horizontal'
          dataSource={documents}
          renderItem={(document) => (
            <DocumentListItem key={document.id} document={document} description={renderDescription(document)} />
          )}
        />
      </DocumentsModal>
    </>
  );
};
